import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowBack,
  ChatBubbleOutline,
  FavoriteBorder,
  PeopleAltOutlined,
  PlayArrow,
  Send,
  StopCircleOutlined,
  VideocamOutlined,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "./UserLivePage.scss";
import { useAuth } from "../context/AuthContext";
import {
  createUserLiveRoom,
  endUserLiveRoom,
  getUserLiveRoom,
  heartbeatUserLiveRoom,
  joinUserLiveRoom,
  leaveUserLiveRoom,
  listUserLiveMessages,
  listUserLiveRooms,
  listUserLiveSignals,
  sendUserLiveMessage,
  sendUserLiveSignal,
  startUserLiveRoom,
} from "../services/userLiveApi";

const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:global.stun.twilio.com:3478" },
  ],
};

function getStoredGuestId() {
  try {
    const existing = localStorage.getItem("streamflix:user-live-guest");
    if (existing) return existing;
    const nextId = `guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("streamflix:user-live-guest", nextId);
    return nextId;
  } catch {
    return `guest-${Date.now()}`;
  }
}

function getUserIdentity(user) {
  return {
    userId: String(user?.userId || user?.id || getStoredGuestId()),
    displayName: user?.nom || user?.username || user?.email || "Spectateur",
  };
}

function serializeSessionDescription(description) {
  if (!description) return {};
  return description.toJSON ? description.toJSON() : { type: description.type, sdp: description.sdp };
}

function serializeIceCandidate(candidate) {
  if (!candidate) return {};
  return candidate.toJSON ? candidate.toJSON() : candidate;
}

function formatRemaining(seconds) {
  const safe = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const rest = Math.floor(safe % 60);
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

function getRemainingSeconds(room, now) {
  if (!room?.expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(room.expiresAt).getTime() - now) / 1000));
}

function UserLivePage() {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const identity = useMemo(() => getUserIdentity(user), [user]);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const viewerPeerRef = useRef(null);
  const hostPeersRef = useRef(new Map());
  const pendingHostIceRef = useRef(new Map());
  const pendingViewerIceRef = useRef([]);
  const lastSignalIdRef = useRef(0);
  const lastMessageIdRef = useRef(0);
  const startedOfferRef = useRef(false);

  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Live",
  });
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Pret");
  const [now, setNow] = useState(Date.now());

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isHost = useMemo(
    () =>
      Boolean(room && String(room.hostUserId) === String(identity.userId)) ||
      queryParams.get("host") === "1",
    [identity.userId, queryParams, room]
  );
  const remainingSeconds = useMemo(() => getRemainingSeconds(room, now), [now, room]);
  const actorPayload = useMemo(
    () => ({
      userId: identity.userId,
      displayName: identity.displayName,
      userName: identity.displayName,
    }),
    [identity.displayName, identity.userId]
  );

  const sendSignal = async (signalType, payload, receiverUserId) => {
    if (!roomId) return;
    await sendUserLiveSignal(roomId, {
      senderUserId: identity.userId,
      receiverUserId,
      signalType,
      payload,
    });
  };

  const closeConnections = () => {
    if (viewerPeerRef.current) {
      viewerPeerRef.current.close();
      viewerPeerRef.current = null;
    }

    hostPeersRef.current.forEach((peer) => peer.close());
    hostPeersRef.current.clear();
    pendingHostIceRef.current.clear();
    pendingViewerIceRef.current = [];

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    startedOfferRef.current = false;
    setIsBroadcasting(false);
  };

  const loadRooms = async () => {
    try {
      const data = await listUserLiveRooms();
      setRooms(data || []);
    } catch (error) {
      console.error("Direct utilisateurs:", error);
    }
  };

  const loadRoom = async () => {
    if (!roomId) return;

    try {
      const data = await getUserLiveRoom(roomId);
      setRoom(data);
    } catch (error) {
      console.error("Room live:", error);
      setConnectionStatus("Direct introuvable ou termine");
    }
  };

  useEffect(() => {
    if (roomId) return undefined;

    loadRooms();
    const timer = window.setInterval(loadRooms, 12000);
    return () => window.clearInterval(timer);
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return undefined;

    loadRoom();
    const timer = window.setInterval(loadRoom, 5000);
    return () => window.clearInterval(timer);
    // The room polling only needs to restart when the route id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      closeConnections();
      if (roomId) {
        leaveUserLiveRoom(roomId, actorPayload).catch(() => {});
      }
    };
  }, [actorPayload, roomId]);

  const createRoom = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      const data = await createUserLiveRoom({
        ...form,
        hostUserId: identity.userId,
        hostName: identity.displayName,
        maxDurationSeconds: 300,
      });

      navigate(`/direct/user/${data.id}?host=1`);
    } catch (error) {
      console.error("Creation Direct:", error);
      alert(error.message || "Impossible de creer le direct.");
    } finally {
      setLoading(false);
    }
  };

  const getOrCreateHostPeer = (viewerId) => {
    const key = String(viewerId);
    const existing = hostPeersRef.current.get(key);
    if (existing) return existing;

    const peer = new RTCPeerConnection(RTC_CONFIG);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        peer.addTrack(track, localStreamRef.current);
      });
    }

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal("ice", serializeIceCandidate(event.candidate), key).catch((error) => {
          console.error("ICE host:", error);
        });
      }
    };

    peer.onconnectionstatechange = () => {
      if (["closed", "failed"].includes(peer.connectionState)) {
        hostPeersRef.current.delete(key);
      }
    };

    hostPeersRef.current.set(key, peer);
    return peer;
  };

  const startBroadcast = async () => {
    if (!roomId) return;

    try {
      setLoading(true);
      setConnectionStatus("Ouverture camera et micro...");
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      localStreamRef.current = mediaStream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = mediaStream;
      }

      const updatedRoom = await startUserLiveRoom(roomId, actorPayload);
      setRoom(updatedRoom);
      setIsBroadcasting(true);
      setConnectionStatus("Vous etes en direct");
      await heartbeatUserLiveRoom(roomId, actorPayload);
    } catch (error) {
      console.error("Start broadcast:", error);
      alert(error.message || "Impossible de demarrer le direct.");
      closeConnections();
    } finally {
      setLoading(false);
    }
  };

  const stopBroadcast = async () => {
    try {
      await endUserLiveRoom(roomId, actorPayload);
      await loadRoom();
    } catch (error) {
      console.error("Stop broadcast:", error);
    } finally {
      closeConnections();
      setConnectionStatus("Direct termine");
    }
  };

  const handleHostSignal = async (signal) => {
    if (!localStreamRef.current || !isBroadcasting) return;

    if (signal.signalType === "offer") {
      const peer = getOrCreateHostPeer(signal.senderUserId);
      await peer.setRemoteDescription(new RTCSessionDescription(signal.payload));
      const queuedIce = pendingHostIceRef.current.get(String(signal.senderUserId)) || [];
      for (const candidate of queuedIce) {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      }
      pendingHostIceRef.current.delete(String(signal.senderUserId));
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      await sendSignal("answer", serializeSessionDescription(peer.localDescription), signal.senderUserId);
      return;
    }

    if (signal.signalType === "ice") {
      const peer = hostPeersRef.current.get(String(signal.senderUserId));
      if (peer?.remoteDescription?.type && signal.payload?.candidate) {
        await peer.addIceCandidate(new RTCIceCandidate(signal.payload));
        return;
      }

      if (signal.payload?.candidate) {
        const key = String(signal.senderUserId);
        pendingHostIceRef.current.set(key, [...(pendingHostIceRef.current.get(key) || []), signal.payload]);
      }
    }
  };

  const startViewerConnection = async () => {
    if (!roomId || !room || startedOfferRef.current || isHost || room.status !== "live") return;

    try {
      startedOfferRef.current = true;
      setConnectionStatus("Connexion au createur...");
      await joinUserLiveRoom(roomId, actorPayload);

      const peer = new RTCPeerConnection(RTC_CONFIG);
      viewerPeerRef.current = peer;
      peer.addTransceiver("video", { direction: "recvonly" });
      peer.addTransceiver("audio", { direction: "recvonly" });

      const remoteStream = new MediaStream();
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }

      peer.ontrack = (event) => {
        event.streams?.[0]?.getTracks().forEach((track) => {
          if (!remoteStream.getTracks().some((current) => current.id === track.id)) {
            remoteStream.addTrack(track);
          }
        });
        setConnectionStatus("Direct connecte");
      };

      peer.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal("ice", serializeIceCandidate(event.candidate), room.hostUserId).catch((error) => {
            console.error("ICE viewer:", error);
          });
        }
      };

      peer.onconnectionstatechange = () => {
        if (peer.connectionState === "connected") {
          setConnectionStatus("Direct connecte");
        }

        if (["failed", "disconnected"].includes(peer.connectionState)) {
          setConnectionStatus("Connexion instable");
        }
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      await sendSignal("offer", serializeSessionDescription(peer.localDescription), room.hostUserId);
    } catch (error) {
      console.error("Viewer WebRTC:", error);
      setConnectionStatus("Connexion impossible");
      startedOfferRef.current = false;
    }
  };

  const handleViewerSignal = async (signal) => {
    const peer = viewerPeerRef.current;
    if (!peer) return;

    if (signal.signalType === "answer") {
      await peer.setRemoteDescription(new RTCSessionDescription(signal.payload));
      for (const candidate of pendingViewerIceRef.current) {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      }
      pendingViewerIceRef.current = [];
      return;
    }

    if (signal.signalType === "ice" && signal.payload?.candidate) {
      if (peer.remoteDescription?.type) {
        await peer.addIceCandidate(new RTCIceCandidate(signal.payload));
      } else {
        pendingViewerIceRef.current = [...pendingViewerIceRef.current, signal.payload];
      }
    }
  };

  useEffect(() => {
    if (!roomId || !room) return undefined;

    const pollSignals = async () => {
      try {
        const signals = await listUserLiveSignals(roomId, {
          userId: identity.userId,
          sinceId: lastSignalIdRef.current,
        });

        for (const signal of signals || []) {
          lastSignalIdRef.current = Math.max(lastSignalIdRef.current, Number(signal.id || 0));
          if (isHost) {
            await handleHostSignal(signal);
          } else {
            await handleViewerSignal(signal);
          }
        }
      } catch (error) {
        console.error("Signal polling:", error);
      }
    };

    pollSignals();
    const timer = window.setInterval(pollSignals, 1300);
    return () => window.clearInterval(timer);
    // WebRTC peers live in refs; polling is intentionally bound to the current room/host state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.userId, isBroadcasting, isHost, room, roomId]);

  useEffect(() => {
    startViewerConnection();
    // The viewer offer is guarded by startedOfferRef to avoid renegotiating on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost, room?.status, roomId]);

  useEffect(() => {
    if (!roomId || room?.status !== "live") return undefined;

    const beat = async () => {
      try {
        const data = await heartbeatUserLiveRoom(roomId, actorPayload);
        if (data?.room) {
          setRoom(data.room);
        }
      } catch (error) {
        console.error("Heartbeat live:", error);
      }
    };

    beat();
    const timer = window.setInterval(beat, 8000);
    return () => window.clearInterval(timer);
  }, [actorPayload, room?.status, roomId]);

  useEffect(() => {
    if (!roomId) return undefined;

    const pollMessages = async () => {
      try {
        const data = await listUserLiveMessages(roomId, {
          sinceId: lastMessageIdRef.current,
        });

        if (data?.length) {
          lastMessageIdRef.current = Math.max(...data.map((item) => Number(item.id || 0)), lastMessageIdRef.current);
          setMessages((current) => {
            const seen = new Set(current.map((item) => item.id));
            return [...current, ...data.filter((item) => !seen.has(item.id))].slice(-120);
          });
        }
      } catch (error) {
        console.error("Messages live:", error);
      }
    };

    pollMessages();
    const timer = window.setInterval(pollMessages, 2200);
    return () => window.clearInterval(timer);
  }, [roomId]);

  const sendComment = async (event) => {
    event.preventDefault();
    const text = message.trim();
    if (!text || !roomId) return;

    try {
      const created = await sendUserLiveMessage(roomId, {
        ...actorPayload,
        message: text,
        messageType: "comment",
      });
      setMessages((current) => [...current, created].slice(-120));
      lastMessageIdRef.current = Math.max(lastMessageIdRef.current, Number(created.id || 0));
      setMessage("");
    } catch (error) {
      console.error("Commentaire live:", error);
    }
  };

  const sendReaction = async (reactionType) => {
    if (!roomId) return;

    try {
      await sendUserLiveMessage(roomId, {
        ...actorPayload,
        messageType: "reaction",
        reactionType,
      });
    } catch (error) {
      console.error("Reaction live:", error);
    }
  };

  if (!roomId) {
    return (
      <main className="user-live-lobby">
        <div className="user-live-lobby__hero">
          <Link to="/direct" className="user-live__back">
            <ArrowBack /> Retour chaines direct
          </Link>
          <span className="user-live__liveBadge">
            <span /> Direct utilisateur
          </span>
          <h1>Creer un live de 5 minutes pour votre communaute.</h1>
          <p>
            Lancez camera et micro, puis les autres utilisateurs peuvent rejoindre, commenter et reagir en direct.
          </p>
        </div>

        <section className="user-live-lobby__grid">
          <form className="user-live-form" onSubmit={createRoom}>
            <div>
              <p>Studio createur</p>
              <h2>Nouveau direct</h2>
            </div>
            <label>
              Titre du direct
              <input
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Ex: Discussion cinema du soir"
                maxLength={180}
                required
              />
            </label>
            <label>
              Categorie
              <input
                value={form.category}
                onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                placeholder="Live, cinema, sport..."
              />
            </label>
            <label>
              Description
              <textarea
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Annoncez le sujet, le contexte ou les regles du live."
                rows={5}
              />
            </label>
            <button type="submit" disabled={loading}>
              <VideocamOutlined /> {loading ? "Creation..." : "Creer le direct"}
            </button>
          </form>

          <div className="user-live-active">
            <div className="user-live-active__header">
              <p>En ce moment</p>
              <h2>Lives utilisateurs actifs</h2>
            </div>
            {rooms.length ? (
              rooms.map((activeRoom) => (
                <Link key={activeRoom.id} to={`/direct/user/${activeRoom.id}`} className="user-live-room-card">
                  <span className="user-live__liveBadge">
                    <span /> Live
                  </span>
                  <strong>{activeRoom.title}</strong>
                  <p>{activeRoom.description || "Rejoindre ce direct maintenant."}</p>
                  <small>
                    <PeopleAltOutlined /> {activeRoom.viewerCount || 0} connectes
                  </small>
                </Link>
              ))
            ) : (
              <div className="user-live-empty">
                <VideocamOutlined />
                <p>Aucun live utilisateur actif pour le moment.</p>
              </div>
            )}
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="user-live-watch">
      <video
        ref={isHost ? localVideoRef : remoteVideoRef}
        className="user-live-watch__video"
        autoPlay
        playsInline
        muted={isHost}
      />

      <div className="user-live-watch__shade user-live-watch__shade--top" />
      <div className="user-live-watch__shade user-live-watch__shade--bottom" />

      <header className="user-live-watch__top">
        <button className="user-live-watch__round" onClick={() => navigate("/direct/create")}>
          <ArrowBack />
        </button>
        <div>
          <span className="user-live__liveBadge">
            <span /> {room?.status === "live" ? "En direct" : "Hors antenne"}
          </span>
          <h1>{room?.title || "Direct utilisateur"}</h1>
          <p>{connectionStatus}</p>
        </div>
      </header>

      <aside className="user-live-watch__rail">
        <div className="user-live-watch__counter">
          <PeopleAltOutlined />
          <span>{room?.viewerCount || 0}</span>
        </div>
        <div className="user-live-watch__counter">
          <ChatBubbleOutline />
          <span>{messages.filter((item) => item.messageType === "comment").length}</span>
        </div>
        <button onClick={() => sendReaction("like")} className="user-live-watch__round" aria-label="J'aime">
          <FavoriteBorder />
        </button>
      </aside>

      <section className="user-live-watch__caption">
        <span>{room?.category || "Live"}</span>
        <h2>{room?.description || "Direct communautaire Streamflix."}</h2>
        <div>
          <strong>{formatRemaining(remainingSeconds)}</strong>
          <small>Temps restant sur 5 min</small>
        </div>
      </section>

      {isHost && !isBroadcasting ? (
        <div className="user-live-start">
          <span className="user-live__liveBadge">
            <span /> Studio live
          </span>
          <h2>Votre direct est pret.</h2>
          <p>Demarrez lorsque vous etes pret. La limite actuelle est de 5 minutes.</p>
          <button onClick={startBroadcast} disabled={loading}>
            <PlayArrow /> {loading ? "Preparation..." : room?.status === "live" ? "Reprendre la diffusion" : "Demarrer le direct"}
          </button>
        </div>
      ) : null}

      {isHost && isBroadcasting ? (
        <button className="user-live-stop" onClick={stopBroadcast}>
          <StopCircleOutlined /> Terminer
        </button>
      ) : null}

      {!isHost && room?.status !== "live" ? (
        <div className="user-live-start">
          <span className="user-live__liveBadge">
            <span /> En attente
          </span>
          <h2>Ce direct n'a pas encore commence.</h2>
          <p>Restez sur la page, la connexion se fera automatiquement quand le createur demarrera.</p>
        </div>
      ) : null}

      <aside className="user-live-chat">
        <div className="user-live-chat__header">
          <p>Live chat</p>
          <h3>Commentaires et avis</h3>
        </div>
        <div className="user-live-chat__messages">
          {messages.length ? (
            messages.map((item) =>
              item.messageType === "reaction" ? (
                <div key={item.id} className="user-live-chat__reaction">
                  {item.userName} a reagi: {item.reactionType}
                </div>
              ) : (
                <div key={item.id} className="user-live-chat__message">
                  <strong>{item.userName}</strong>
                  <p>{item.message}</p>
                </div>
              )
            )
          ) : (
            <div className="user-live-chat__empty">Soyez le premier a commenter.</div>
          )}
        </div>
        <div className="user-live-chat__reactions">
          {["like", "wow", "top"].map((reaction) => (
            <button key={reaction} onClick={() => sendReaction(reaction)}>
              {reaction}
            </button>
          ))}
        </div>
        <form className="user-live-chat__form" onSubmit={sendComment}>
          <input
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Ecrire un commentaire..."
            maxLength={500}
          />
          <button type="submit" aria-label="Envoyer">
            <Send />
          </button>
        </form>
      </aside>
    </main>
  );
}

export default UserLivePage;
